import { stableSortAlbumsByDisplayName } from './helpers/album-natural-sort'
import type { AlbumSearchMode } from './types/album-search-mode'

/**
 * Strip-with-no-split punctuation
 */
export const STRIP_NO_SPLIT_CHARS = "'\u2018\u2019\u02BC´`!#()"

/** Special characters that trigger raw match mode when no letters/numbers remain after normalization. */
export const RAW_MATCH_SPECIAL_CHARS = new Set("!#()&.,-/@'\u2018\u2019\u02BC´`")

/** Separators that become spaces (interchangeable word boundaries). */
const SEPARATOR_CHARS = '[_&.,\\-/]'

const STRIP_NO_SPLIT_RE = new RegExp(`[${STRIP_NO_SPLIT_CHARS}]`, 'g')
const SEPARATOR_RE = new RegExp(SEPARATOR_CHARS, 'g')

export function normalizeAlbumName(name: string): string {
  let value = name.toLowerCase()

  value = value.replace(STRIP_NO_SPLIT_RE, '')
  value = value.replace(SEPARATOR_RE, ' ')
  value = value.replace(/\s+/g, ' ').trim()
  return value
}

/**
 * Tokenize for Image Service literal substring search.
 * Strip-chars are treated as spaces here (not concatenated) so an API query of
 * `mary` can find `Mary's`, while client-side {@link normalizeAlbumName} still
 * requires the full stripped term (`marys`).
 */
export function filenameSearchApiTokens(name: string): string[] {
  let value = name.toLowerCase()

  value = value.replace(STRIP_NO_SPLIT_RE, ' ')
  value = value.replace(SEPARATOR_RE, ' ')
  value = value.replace(/\s+/g, ' ').trim()

  return value.split(' ').filter(Boolean)
}

export function getAlbumSearchMode(search: string): AlbumSearchMode {
  const trimmed = search.trim()

  if (!trimmed) return 'none'

  const normalized = normalizeAlbumName(trimmed)

  if (normalized.length > 0) return 'normalized'

  const hasSpecial = [...trimmed].some((char) => RAW_MATCH_SPECIAL_CHARS.has(char))

  return hasSpecial ? 'raw' : 'none'
}

function uniqueRawSearchChars(search: string): string[] {
  const seen = new Set<string>()
  const chars: string[] = []

  for (const char of search) {
    if (!RAW_MATCH_SPECIAL_CHARS.has(char) || seen.has(char)) continue
    seen.add(char)
    chars.push(char)
  }

  return chars
}

export function matchesAlbumRaw(displayName: string, search: string): boolean {
  const chars = uniqueRawSearchChars(search)

  if (chars.length === 0) return true
  return chars.some((char) => displayName.includes(char))
}

export function matchesAlbumNormalized(displayName: string, search: string): boolean {
  const normalizedSearch = normalizeAlbumName(search)

  if (!normalizedSearch) return true
  return normalizeAlbumName(displayName).includes(normalizedSearch)
}

export function sortAlbumDisplayNames<T extends { displayName: string }>(items: T[]): T[] {
  return stableSortAlbumsByDisplayName(items)
}

export function filterAlbumsBySearch<T extends { displayName: string }>(
  items: T[],
  search: string,
  options?: { isCurated?: boolean }
): T[] {
  const mode = getAlbumSearchMode(search)

  if (mode === 'none') {
    return sortAlbumDisplayNames(items)
  }

  if (mode === 'raw') {
    if (options?.isCurated) return []
    return sortAlbumDisplayNames(items.filter((item) => matchesAlbumRaw(item.displayName, search)))
  }

  return sortAlbumDisplayNames(
    items.filter((item) => matchesAlbumNormalized(item.displayName, search))
  )
}
